/**
 * useRoomPromptsToday Hook
 * 
 * Fetches today's room prompts from the backend.
 * 
 * BUG FIX (Jan 2026): 
 * - Backend returns an ARRAY of TodayPromptOut objects, not a {prompts: {}} map
 * - Added date-change detection to refetch when day changes
 * - Added periodic refetch to handle midnight rollover
 */

import { useState, useEffect, useCallback, useContext, useRef, useMemo } from 'react';
import apiClient from "../api/client";
import { AuthContext } from '../App';


// Get current date in YYYY-MM-DD format (client timezone)
function getLocalDateString() {
  const now = new Date();
  return now.toISOString().split('T')[0];
}

export function useRoomPromptsToday() {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [promptsArray, setPromptsArray] = useState([]); // Raw array from backend
  const [serverDate, setServerDate] = useState(null); // Date from server response
  const lastFetchDateRef = useRef(null); // Track when we last fetched

  const fetchPrompts = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      // Add cache-busting query param to ensure fresh data
      const cacheBuster = Date.now();
      const res = await apiClient.get(`/rooms/prompts/today?_t=${cacheBuster}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
      });
      
      // Backend returns an array of TodayPromptOut objects
      const data = Array.isArray(res.data) ? res.data : [];
      setPromptsArray(data);
      
      // Extract server date from first prompt (they all have the same date)
      const firstPrompt = data[0];
      if (firstPrompt?.date) {
        setServerDate(firstPrompt.date);
      }
      
      lastFetchDateRef.current = getLocalDateString();
      setError(null);
    } catch (err) {
      console.error('[useRoomPromptsToday] Failed to fetch:', err);
      setError(err?.response?.data?.detail || 'Failed to load prompts');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Initial fetch
  useEffect(() => {
    fetchPrompts();
  }, [fetchPrompts]);

  // Calculate milliseconds until next midnight in given timezone
  const getMsUntilMidnight = useCallback((timezone = 'America/Chicago') => {
    try {
      const now = new Date();
      
      // Get current time in target timezone
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
      
      const parts = formatter.formatToParts(now);
      const getPart = (type) => parts.find(p => p.type === type)?.value;
      
      // Construct midnight of next day in that timezone
      const year = parseInt(getPart('year'));
      const month = parseInt(getPart('month')) - 1;
      const day = parseInt(getPart('day'));
      const hour = parseInt(getPart('hour'));
      const minute = parseInt(getPart('minute'));
      const second = parseInt(getPart('second'));
      
      // Calculate seconds until midnight
      const secondsUntilMidnight = (24 - hour - 1) * 3600 + (60 - minute - 1) * 60 + (60 - second);
      const msUntilMidnight = secondsUntilMidnight * 1000;
      
      // Add a small buffer (5 seconds) to ensure we're past midnight
      return msUntilMidnight + 5000;
    } catch (err) {
      console.warn('[useRoomPromptsToday] Failed to calculate midnight, using fallback', err);
      // Fallback: 30 minutes
      return 30 * 60 * 1000;
    }
  }, []);

  // Smart refetch: visibility change + next midnight timer (no polling)
  useEffect(() => {
    if (!token) return;
    
    let midnightTimeoutId = null;
    
    // Schedule refetch at next midnight
    const scheduleMidnightRefetch = () => {
      // Use timezone from server response if available, otherwise default
      const timezone = serverDate ? 'America/New_York' : 'America/Chicago';
      const msUntilMidnight = getMsUntilMidnight(timezone);
      
      console.log(`[useRoomPromptsToday] Scheduling refetch in ${Math.round(msUntilMidnight / 60000)} minutes (at midnight)`);
      
      midnightTimeoutId = setTimeout(() => {
        console.log('[useRoomPromptsToday] Midnight reached, refetching prompts');
        fetchPrompts().then(() => {
          // Reschedule for the next midnight after refetch
          scheduleMidnightRefetch();
        });
      }, msUntilMidnight);
    };
    
    // Initial schedule
    scheduleMidnightRefetch();
    
    // Visibility change handler - refetch when tab becomes visible if date changed
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const currentDate = getLocalDateString();
        if (lastFetchDateRef.current && lastFetchDateRef.current !== currentDate) {
          console.log('[useRoomPromptsToday] Tab visible + date changed, refetching');
          fetchPrompts();
          // Reschedule midnight timer after visibility refetch
          if (midnightTimeoutId) {
            clearTimeout(midnightTimeoutId);
          }
          scheduleMidnightRefetch();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (midnightTimeoutId) {
        clearTimeout(midnightTimeoutId);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [token, fetchPrompts, serverDate, getMsUntilMidnight]);

  // Convert array to map for easy lookup by room_key
  const promptsMap = useMemo(() => {
    const map = {};
    for (const prompt of promptsArray) {
      if (prompt.room_key) {
        map[prompt.room_key] = prompt;
      }
    }
    return map;
  }, [promptsArray]);

  // Get prompt text for a specific room (tries multiple key formats)
  const getPromptForRoom = useCallback((roomKey) => {
    if (!roomKey) return null;
    
    // Try exact match first
    if (promptsMap[roomKey]) {
      return promptsMap[roomKey].prompt_text;
    }
    
    // Try normalized key (snake_case)
    const normalizedKey = roomKey.toLowerCase().replace(/-/g, '_');
    if (promptsMap[normalizedKey]) {
      return promptsMap[normalizedKey].prompt_text;
    }
    
    // Try hyphenated version
    const hyphenatedKey = roomKey.toLowerCase().replace(/_/g, '-');
    if (promptsMap[hyphenatedKey]) {
      return promptsMap[hyphenatedKey].prompt_text;
    }
    
    return null;
  }, [promptsMap]);

  // Get full prompt object for a room (includes source, cycle_day, cycle_length)
  const getFullPromptForRoom = useCallback((roomKey) => {
    if (!roomKey) return null;
    
    // Try exact match first
    if (promptsMap[roomKey]) {
      return promptsMap[roomKey];
    }
    
    // Try normalized key (snake_case)
    const normalizedKey = roomKey.toLowerCase().replace(/-/g, '_');
    if (promptsMap[normalizedKey]) {
      return promptsMap[normalizedKey];
    }
    
    // Try hyphenated version
    const hyphenatedKey = roomKey.toLowerCase().replace(/_/g, '-');
    if (promptsMap[hyphenatedKey]) {
      return promptsMap[hyphenatedKey];
    }
    
    return null;
  }, [promptsMap]);

  // Check if a prompt exists for a room
  const hasPromptForRoom = useCallback((roomKey) => {
    return getPromptForRoom(roomKey) !== null;
  }, [getPromptForRoom]);

  return {
    loading,
    error,
    data: promptsArray, // Raw array for backward compat
    refetch: fetchPrompts,
    date: serverDate,
    prompts: promptsMap, // Map for easy lookup
    getPromptForRoom,
    getFullPromptForRoom, // New: returns full prompt object with source, cycle_day, etc.
    hasPromptForRoom,
  };
}

export default useRoomPromptsToday;
